import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '../ui/Dialog.js';
import { Button } from '../ui/Button.js';
import { Field, Input } from '../ui/Field.js';
import { Notice } from '../ui/Notice.js';
import { useToast } from '../ui/useToast.js';
import { TuningPicker } from '../tuning/TuningPicker.js';
import { DifficultyChoiceRow } from './DifficultyChoiceRow.js';
import { EditNoteField } from './EditNoteField.js';
import { SuggestModeNotices } from './SuggestModeNotices.js';
import { useAuthStore } from '../../stores/authStore.js';
import {
  canEditTab,
  type CanEditTabMode,
  type TabViewerUser,
  TAB_EDIT_COPY,
  TAB_TITLE_MAX_LENGTH,
  TAB_ARTIST_MAX_LENGTH,
  EDIT_NOTE_MAX_LENGTH,
  mapTabEditError
} from '../../utils/tabEdits.js';
import { isLocalOnlyTab, toDifficultyLevel } from '../../utils/difficulty.js';
import {
  getMyEditsForTab,
  queueTabEdit,
  applyLocalDirectEdit,
  applyServerTabState
} from '../../db/tabEdits.js';
import { syncTabEdits } from '../../sync/tabEditSync.js';
import { postTabEdit, getTabEdits } from '../../utils/apiClient.js';
import { newId } from '../../utils/id.js';
import type { DifficultyLevel, TabSong } from '../../types/index.js';

export interface TabDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  tab: TabSong;
  initialSection?: 'details' | 'difficulty' | string;
  onSaved?: (tab: TabSong) => void;
}

const TabDetailsDialogInner: React.FC<TabDetailsDialogProps> = ({
  open,
  onClose,
  tab,
  initialSection = 'details',
  onSaved
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isGuest = useAuthStore((s) => s.isGuest);
  const openVerifyDialog = useAuthStore((s) => s.openVerifyDialog);
  const isAdmin = user?.role === 'ADMIN';

  const viewer: TabViewerUser = {
    id: user?.id,
    role: user?.role,
    isGuest,
    emailVerified: user?.emailVerified
  };

  const permDetails = canEditTab(tab, viewer, 'DETAILS', { tabEditor: false });
  const permDifficulty = canEditTab(tab, viewer, 'DIFFICULTY', { tabEditor: false });

  const isUnverified = permDetails.reason === 'unverified' || permDifficulty.reason === 'unverified';
  const mode: CanEditTabMode =
    permDetails.mode === 'direct' || permDifficulty.mode === 'direct' ? 'direct' : 'suggest';
  const isSuggest = mode === 'suggest';

  const [title, setTitle] = useState(tab.title || '');
  const [artist, setArtist] = useState(tab.artist || '');
  const [tuning, setTuning] = useState(tab.tuning || 'E Standard');
  const [rhythm, setRhythm] = useState<DifficultyLevel | null>(tab.rhythmDifficulty ?? null);
  const [lead, setLead] = useState<DifficultyLevel | null>(tab.leadDifficulty ?? null);
  const [note, setNote] = useState('');
  const [hasOwnPending, setHasOwnPending] = useState(false);
  const [adminPendingCount, setAdminPendingCount] = useState<number | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [titleTouched, setTitleTouched] = useState(false);
  const [artistTouched, setArtistTouched] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  const difficultySectionRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAdmin || !open || !tab.id || isLocalOnlyTab(tab)) return;
    getTabEdits(tab.id, { status: 'PENDING' })
      .then((res) => {
        const items = res?.data || [];
        const count = items.filter((e) => e.kind === 'DETAILS' || e.kind === 'DIFFICULTY').length;
        setAdminPendingCount(count);
      })
      .catch((err) => {
        console.warn('TabDetailsDialog: failed to load pending suggestions for admin', err);
      });
  }, [isAdmin, open, tab.id]);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    getMyEditsForTab(tab.id, user.id)
      .then((edits) => {
        const pDetails = edits.find(
          (e) => e.kind === 'DETAILS' && (e.status === 'PENDING' || e.status === 'QUEUED')
        );
        const pDiff = edits.find(
          (e) => e.kind === 'DIFFICULTY' && (e.status === 'PENDING' || e.status === 'QUEUED')
        );
        if (pDetails || pDiff) {
          setHasOwnPending(true);
          if (pDetails?.payload) {
            const payload = pDetails.payload as Record<string, unknown>;
            if (typeof payload.title === 'string') setTitle(payload.title);
            if (typeof payload.artist === 'string') setArtist(payload.artist);
            if (typeof payload.tuning === 'string') setTuning(payload.tuning);
          }
          if (pDiff?.payload) {
            const payload = pDiff.payload as Record<string, unknown>;
            const r = payload.rhythm_difficulty ?? payload.rhythmDifficulty;
            if (r !== undefined) setRhythm(toDifficultyLevel(r));
            const l = payload.lead_difficulty ?? payload.leadDifficulty;
            if (l !== undefined) setLead(toDifficultyLevel(l));
          }
          const noteVal = pDetails?.note || pDiff?.note;
          if (noteVal) setNote(noteVal);
        }
      })
      .catch((err) => {
        console.warn('TabDetailsDialog: failed to load pending edits', err);
      });
  }, [tab.id, user?.id]);

  useEffect(() => {
    if (initialSection === 'difficulty') {
      const timer = setTimeout(() => {
        difficultySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const firstRadio = difficultySectionRef.current?.querySelector<HTMLElement>('button[role="radio"]');
        firstRadio?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [initialSection]);

  if (isUnverified) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={TAB_EDIT_COPY.c14}
        titleHeadingLevel="h3"
        size="md"
        data-testid="tab-details-dialog"
        footer={
          <Button variant="secondary" onClick={onClose} data-testid="details-cancel">
            {TAB_EDIT_COPY.c17Close}
          </Button>
        }
      >
        <div className="flex flex-col gap-4 py-2">
          <Notice tone="warning" testId="details-unverified-notice">
            {TAB_EDIT_COPY.c21}
          </Notice>
          <Button
            variant="primary"
            data-testid="details-verify-email"
            onClick={() => {
              openVerifyDialog();
            }}
          >
            {TAB_EDIT_COPY.c21Action}
          </Button>
        </div>
      </Dialog>
    );
  }

  const isTitleChanged = title.trim() !== (tab.title || '').trim();
  const isArtistChanged = artist.trim() !== (tab.artist || '').trim();
  const isTuningChanged = tuning.trim() !== (tab.tuning || '').trim();
  const isRhythmChanged = (rhythm ?? null) !== (tab.rhythmDifficulty ?? null);
  const isLeadChanged = (lead ?? null) !== (tab.leadDifficulty ?? null);

  const isDetailsChanged = isTitleChanged || isArtistChanged || isTuningChanged;
  const isDifficultyChanged = isRhythmChanged || isLeadChanged;
  const hasAnyChange = isDetailsChanged || isDifficultyChanged;

  const adminOffline = isAdmin && mode === 'direct' && !isOnline && !isLocalOnlyTab(tab);

  const titleTrimmed = title.trim();
  const artistTrimmed = artist.trim();

  let titleError: string | null = null;
  if (titleTouched || saving) {
    if (!titleTrimmed) titleError = TAB_EDIT_COPY.limitTitleRequired;
    else if (titleTrimmed.length > TAB_TITLE_MAX_LENGTH) titleError = TAB_EDIT_COPY.limitTitleMax;
  }

  let artistError: string | null = null;
  if (artistTouched || saving) {
    if (!artistTrimmed) artistError = TAB_EDIT_COPY.limitArtistRequired;
    else if (artistTrimmed.length > TAB_ARTIST_MAX_LENGTH) artistError = TAB_EDIT_COPY.limitArtistMax;
  }

  const isNoteValid = note.length <= EDIT_NOTE_MAX_LENGTH;
  const isValid =
    titleTrimmed.length >= 1 &&
    titleTrimmed.length <= TAB_TITLE_MAX_LENGTH &&
    artistTrimmed.length >= 1 &&
    artistTrimmed.length <= TAB_ARTIST_MAX_LENGTH &&
    isNoteValid;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (saving || !hasAnyChange || !isValid || adminOffline) return;
    setSaving(true);
    try {
      const detailsPayload: Record<string, string> = {};
      if (isTitleChanged) detailsPayload.title = titleTrimmed;
      if (isArtistChanged) detailsPayload.artist = artistTrimmed;
      if (isTuningChanged) detailsPayload.tuning = tuning.trim();

      const difficultyPayload: Record<string, any> = {};
      if (isRhythmChanged) difficultyPayload.rhythm_difficulty = rhythm;
      if (isLeadChanged) difficultyPayload.lead_difficulty = lead;

      const noteText = note.trim() || undefined;
      const localOnly = isLocalOnlyTab(tab);

      const updatedTab: TabSong = {
        ...tab,
        ...(isTitleChanged ? { title: titleTrimmed } : {}),
        ...(isArtistChanged ? { artist: artistTrimmed } : {}),
        ...(isTuningChanged ? { tuning: tuning.trim() } : {}),
        ...(isRhythmChanged ? { rhythmDifficulty: rhythm } : {}),
        ...(isLeadChanged ? { leadDifficulty: lead } : {})
      };

      if (localOnly) {
        if (isDetailsChanged) {
          await applyLocalDirectEdit(tab.id, 'DETAILS', detailsPayload);
        }
        if (isDifficultyChanged) {
          await applyLocalDirectEdit(tab.id, 'DIFFICULTY', difficultyPayload);
        }
        toast.success(TAB_EDIT_COPY.c01);
        onSaved?.(updatedTab);
        onClose();
        return;
      }

      if (mode === 'direct' && permDetails.reason === 'owner-private') {
        if (isDetailsChanged) {
          await applyLocalDirectEdit(tab.id, 'DETAILS', detailsPayload);
          await queueTabEdit({
            id: newId(),
            tabId: tab.id,
            ownerId: user!.id,
            kind: 'DETAILS',
            payload: detailsPayload,
            baseRevision: tab.revision ?? 1,
            mode: 'direct',
            status: 'QUEUED'
          });
        }
        if (isDifficultyChanged) {
          await applyLocalDirectEdit(tab.id, 'DIFFICULTY', difficultyPayload);
          await queueTabEdit({
            id: newId(),
            tabId: tab.id,
            ownerId: user!.id,
            kind: 'DIFFICULTY',
            payload: difficultyPayload,
            baseRevision: tab.revision ?? 1,
            mode: 'direct',
            status: 'QUEUED'
          });
        }
        if (isOnline) {
          void syncTabEdits();
          toast.success(TAB_EDIT_COPY.c01);
        } else {
          toast.info(TAB_EDIT_COPY.c04b);
        }
        onSaved?.(updatedTab);
        onClose();
        return;
      }

      if (mode === 'direct' && permDetails.reason === 'admin') {
        if (!isOnline) {
          toast.error(TAB_EDIT_COPY.c05);
          return;
        }
        if (isDetailsChanged) {
          const res = await postTabEdit(tab.id, {
            id: newId(),
            kind: 'DETAILS',
            base_revision: tab.revision ?? 1,
            payload: detailsPayload
          });
          if (res.applied) {
            await applyServerTabState(tab.id, res.tab, { force: true });
          }
        }
        if (isDifficultyChanged) {
          const res = await postTabEdit(tab.id, {
            id: newId(),
            kind: 'DIFFICULTY',
            base_revision: tab.revision ?? 1,
            payload: difficultyPayload
          });
          if (res.applied) {
            await applyServerTabState(tab.id, res.tab, { force: true });
          }
        }
        toast.success(TAB_EDIT_COPY.c01);
        onSaved?.(updatedTab);
        onClose();
        return;
      }

      // Suggest mode
      if (isDetailsChanged) {
        await queueTabEdit({
          id: newId(),
          tabId: tab.id,
          ownerId: user!.id,
          kind: 'DETAILS',
          payload: detailsPayload,
          baseRevision: tab.revision ?? 1,
          note: noteText,
          mode: 'suggest',
          status: 'QUEUED'
        });
      }
      if (isDifficultyChanged) {
        await queueTabEdit({
          id: newId(),
          tabId: tab.id,
          ownerId: user!.id,
          kind: 'DIFFICULTY',
          payload: difficultyPayload,
          baseRevision: tab.revision ?? 1,
          note: noteText,
          mode: 'suggest',
          status: 'QUEUED'
        });
      }

      if (isOnline) {
        void syncTabEdits();
        toast.success(TAB_EDIT_COPY.c03);
      } else {
        toast.info(TAB_EDIT_COPY.c04);
      }
      onSaved?.(tab);
      onClose();
    } catch (err) {
      toast.error(mapTabEditError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={TAB_EDIT_COPY.c14}
      titleHeadingLevel="h3"
      size="md"
      data-testid="tab-details-dialog"
      footer={
        <>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            loading={saving}
            disabled={!hasAnyChange || !isValid || adminOffline}
            data-testid="details-save"
          >
            {isSuggest ? TAB_EDIT_COPY.c17Suggest : TAB_EDIT_COPY.c17Direct}
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="details-cancel"
          >
            {TAB_EDIT_COPY.c17Cancel}
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-5">
        {/* SuggestModeNotices */}
        <SuggestModeNotices
          mode={mode}
          hasOwnPending={hasOwnPending}
          adminPendingCount={adminPendingCount}
          adminOffline={adminOffline}
          onNavigateToEditsQueue={() => {
            onClose();
            navigate(`/admin/moderation?queue=edits&tab=${tab.id}`);
          }}
        />

        {/* Section «مشخصات» */}
        <section className="flex flex-col gap-3">
          <h4 className="text-sm font-semibold text-base-content">
            {TAB_EDIT_COPY.c15Details}
          </h4>

          <Field
            label={
              <span className="flex items-center gap-1.5">
                <span>{TAB_EDIT_COPY.c16Title}</span>
                {isTitleChanged && (
                  <span
                    data-testid="details-changed-title"
                    className="size-1.5 rounded-full bg-accent inline-block"
                    aria-hidden="true"
                  >
                    <span className="sr-only">تغییر کرده</span>
                  </span>
                )}
              </span>
            }
            required
            error={titleError || undefined}
          >
            <Input
              ref={titleInputRef}
              type="text"
              dir="auto"
              data-testid="details-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleTouched(true);
              }}
              autoFocus={initialSection !== 'difficulty'}
              required
              error={Boolean(titleError)}
            />
          </Field>

          <Field
            label={
              <span className="flex items-center gap-1.5">
                <span>{TAB_EDIT_COPY.c16Artist}</span>
                {isArtistChanged && (
                  <span
                    data-testid="details-changed-artist"
                    className="size-1.5 rounded-full bg-accent inline-block"
                    aria-hidden="true"
                  >
                    <span className="sr-only">تغییر کرده</span>
                  </span>
                )}
              </span>
            }
            required
            error={artistError || undefined}
          >
            <Input
              type="text"
              dir="auto"
              data-testid="details-artist"
              value={artist}
              onChange={(e) => {
                setArtist(e.target.value);
                setArtistTouched(true);
              }}
              required
              error={Boolean(artistError)}
            />
          </Field>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-base-content/80 flex items-center gap-1.5">
              <span>{TAB_EDIT_COPY.c16Tuning}</span>
              {isTuningChanged && (
                <span
                  data-testid="details-changed-tuning"
                  className="size-1.5 rounded-full bg-accent inline-block"
                  aria-hidden="true"
                >
                  <span className="sr-only">تغییر کرده</span>
                </span>
              )}
            </span>
            <TuningPicker
              value={tuning}
              onChange={(name) => setTuning(name)}
              selectTestId="details-tuning"
              testIdPrefix="details-tuning"
            />
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-base-300" />

        {/* Section «سطح سختی» */}
        <section id="details-difficulty" ref={difficultySectionRef} className="flex flex-col gap-3">
          <h4 className="text-sm font-semibold text-base-content flex items-center gap-1.5">
            <span>{TAB_EDIT_COPY.c15Difficulty}</span>
            {(isRhythmChanged || isLeadChanged) && (
              <span
                data-testid="details-changed-difficulty"
                className="size-1.5 rounded-full bg-accent inline-block"
                aria-hidden="true"
              >
                <span className="sr-only">تغییر کرده</span>
              </span>
            )}
          </h4>

          <div className="relative">
            {isRhythmChanged && (
              <span
                data-testid="details-changed-rhythm"
                className="absolute top-1 start-20 size-1.5 rounded-full bg-accent inline-block z-10"
                aria-hidden="true"
              >
                <span className="sr-only">تغییر کرده</span>
              </span>
            )}
            <DifficultyChoiceRow
              kind="rhythm"
              value={rhythm}
              onChange={setRhythm}
              disabled={saving}
            />
          </div>

          <div className="relative">
            {isLeadChanged && (
              <span
                data-testid="details-changed-lead"
                className="absolute top-1 start-20 size-1.5 rounded-full bg-accent inline-block z-10"
                aria-hidden="true"
              >
                <span className="sr-only">تغییر کرده</span>
              </span>
            )}
            <DifficultyChoiceRow
              kind="lead"
              value={lead}
              onChange={setLead}
              disabled={saving}
            />
          </div>
        </section>

        {/* EditNoteField in suggest mode only */}
        {isSuggest && (
          <EditNoteField
            data-testid="details-note"
            value={note}
            onChange={setNote}
            disabled={saving}
          />
        )}

        {/* C-13 hint when no changes */}
        {!hasAnyChange && (
          <p
            role="status"
            data-testid="details-nochange-hint"
            className="text-xs text-base-content/60"
          >
            {TAB_EDIT_COPY.c13}
          </p>
        )}
      </form>
    </Dialog>
  );
};

export const TabDetailsDialog: React.FC<TabDetailsDialogProps> = (props) => {
  if (!props.open) return null;
  return <TabDetailsDialogInner key={props.tab.id} {...props} />;
};

TabDetailsDialog.displayName = 'TabDetailsDialog';
